import React, { createRef, useCallback, useMemo, useState } from 'react'
import { Keyboard, LayoutChangeEvent, Platform, StyleSheet, View, ViewStyle, useWindowDimensions } from 'react-native'
import {
  BottomSheetBackdrop,
  BottomSheetFooter,
  BottomSheetHandleProps,
  BottomSheetModal,
  BottomSheetView
} from '@gorhom/bottom-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ReduceMotion } from 'react-native-reanimated'
import { isNumber } from 'lodash'

import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { ChatContent, ChatFooter } from '../../components/ANewDesign/BottomModalContent/Content/Chats'
import { ChatHandle } from '../../components/ANewDesign/BottomModalContent/Content/Chats/ChatHandle'
import { CommandsContextProvider } from '../new-design/hooks/useCommonCommands'
import { palette } from '../../theme/palette'

const SHEET_BASE_HEIGHT = 400
const SHEET_LIST_HEIGHT = SHEET_BASE_HEIGHT - 100

export const chatLogsSheetRef = createRef<BottomSheetModal>()
const chatLogsSheetIsOpenRef = createRef<boolean>()
chatLogsSheetIsOpenRef.current = false

export const useChatLogsSheet = () => {
  const open = useCallback(() => {
    chatLogsSheetIsOpenRef.current = true
    chatLogsSheetRef.current?.present()
  }, [])

  const close = useCallback(() => {
    chatLogsSheetIsOpenRef.current = false
    chatLogsSheetRef.current?.close()
  }, [])

  const toggle = useCallback(() => {
    if (chatLogsSheetIsOpenRef.current) close()
    else open()
  }, [open, close])

  return {
    ref: chatLogsSheetRef,
    open,
    close,
    toggle,
    isOpen: chatLogsSheetIsOpenRef.current
  }
}

export const ChatLogsSheet = () => {
  const insets = useSafeAreaInsets()
  const windowSize = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const _maxWidth = isNumber(maxWidth) ? maxWidth : windowSize.width
  const marginHorizontal = windowSize.width > _maxWidth ? (windowSize.width - _maxWidth) / 2 : 0

  const snapPoints = useMemo(() => {
    const half = Math.floor(windowSize.height * 0.5)
    const full = windowSize.height - insets.top
    return [half, full]
  }, [windowSize, insets.top])

  const onInputFocus = useCallback(() => {
    // NOT using: using dynamic sizing
    // chatLogsSheetRef.current?.snapToIndex(1)
  }, [])

  const onInputBlur = useCallback(() => {
    Keyboard.dismiss()
  }, [])

  const bottomPadding = Math.max(insets.bottom, 16)

  const renderFooter = useCallback(
    (props: React.ComponentProps<typeof BottomSheetFooter>) => (
      // This padding pushes Footer up above the iOS "task switcher" handle
      <BottomSheetFooter {...props} bottomInset={bottomPadding}>
        {/* Container for input field (removed extra padding - PK) */}
        {/* [styles.footerContainer, {paddingBottom: bottomPadding}] */}
        <View style={styles.footerContainer}>
          <CommandsContextProvider context="convex">
            <ChatFooter onInputFocus={onInputFocus} onInputBlur={onInputBlur} />
          </CommandsContextProvider>
        </View>
      </BottomSheetFooter>
    ),
    [bottomPadding, onInputFocus, onInputBlur]
  )

  const renderHandle = useCallback((props: BottomSheetHandleProps) => {
    return <ChatHandle {...props} style={{ backgroundColor: DS.palette['Brand/Cream'] }} />
  }, [])

  const renderBackdrop = useCallback((props: React.ComponentProps<typeof BottomSheetBackdrop>) => {
    return <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.1} />
  }, [])

  const onDismiss = useCallback(() => {
    chatLogsSheetIsOpenRef.current = false
  }, [])

  /*
  HEADS UP! So many issues with the bottom sheet are due to the BottomSheetView below being missing
  and trying to use snapPoints (using dynamic sizing works better). 
  
  There's an active issue (e.g. not solved!!) about it here:
  https://github.com/gorhom/react-native-bottom-sheet/issues/2539 
  */

  return (
    <BottomSheetModal
      ref={chatLogsSheetRef}
      onDismiss={onDismiss}
      enableDynamicSizing={true}
      maxDynamicContentSize={SHEET_BASE_HEIGHT}
      // snapPoints={snapPoints}
      backdropComponent={renderBackdrop}
      overrideReduceMotion={ReduceMotion.System}
      keyboardBehavior={Platform.OS === 'android' ? 'extend' : 'interactive'}
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
      enableDismissOnClose={true}
      enableBlurKeyboardOnGesture={false}
      enableContentPanningGesture={false}
      enableHandlePanningGesture={true}
      enableOverDrag
      enablePanDownToClose={true}
      handleComponent={renderHandle}
      footerComponent={renderFooter}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      containerStyle={{
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined
      }}
      style={{
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <BottomSheetView>
        <ChatContent clearanceOverride={undefined} listHeight={SHEET_LIST_HEIGHT} />
      </BottomSheetView>
    </BottomSheetModal>
  )
}

const styles = StyleSheet.create({
  footerContainer: {
    backgroundColor: 'transparent',
    paddingVertical: 8
  }
})
